import React from 'react';
import { Button, Tooltip } from 'antd';
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { PDFLink } from 'components/RevxPDFLink';
import { useInvoiceDownload } from 'hooks/useInvoiceDownload';

export interface InvoiceFileBlockProps {
  fileUrl: string;
  title: string;
  jobId: string | number;
  type: 'send' | 'sendQuickBooks' | 'receive' | 'receiveQuickBooks';
  show?: boolean;
  expired?: boolean;
  invoiceNumber?: string;
  batchUploadInvoiceNumber?: string;
}

export const InvoiceFileBlock: React.FC<InvoiceFileBlockProps> = ({
  fileUrl,
  title,
  jobId,
  type,
  show = true,
  expired = false,
  invoiceNumber,
  batchUploadInvoiceNumber,
}) => {
  const {
    getPDFLink,
    handleDownloadSendInvoice,
    handleDownloadSendQuickBooksInvoice,
    handleDownloadReceiveInvoice,
    handleDownloadReceiveQuickBooksInvoice,
    sendInvoiceIsDownloading,
    sendQuickBooksInvoiceIsDownloading,
    receiveInvoiceIsDownloading,
    receiveQuickBooksInvoiceIsDownloading,
  } = useInvoiceDownload();

  if (!show || !fileUrl) {
    return null;
  }

  const getHandler = () => {
    switch (type) {
      case 'send':
        return () => handleDownloadSendInvoice(fileUrl, jobId);
      case 'sendQuickBooks':
        return () => handleDownloadSendQuickBooksInvoice(fileUrl, jobId);
      case 'receive':
        return () => handleDownloadReceiveInvoice(fileUrl, jobId);
      case 'receiveQuickBooks':
        return () => handleDownloadReceiveQuickBooksInvoice(fileUrl, jobId);
      default:
        return () => {};
    }
  };

  const getLoadingState = () => {
    switch (type) {
      case 'send':
        return sendInvoiceIsDownloading;
      case 'sendQuickBooks':
        return sendQuickBooksInvoiceIsDownloading;
      case 'receive':
        return receiveInvoiceIsDownloading;
      case 'receiveQuickBooks':
        return receiveQuickBooksInvoiceIsDownloading;
      default:
        return false;
    }
  };

  const getPDFLinkDom = (fileUrl: string, title: string) => {
    return <PDFLink title={title} link={getPDFLink(fileUrl)} />;
  };

  return (
    <div
      className="card-pdf-item"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1px',
        }}
      >
        {getPDFLinkDom(fileUrl, title)}
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          onClick={getHandler()}
          title="Download Invoice PDF"
          loading={getLoadingState()}
          style={{ width: '18px', marginLeft: '1px' }}
        />
        {expired && (
          <Tooltip title="Job has been adjusted, please regenerate and share">
            <ExclamationCircleOutlined
              style={{
                color: '#ff4d4f',
                fontSize: '14px',
                cursor: 'help',
                marginLeft: '4px',
              }}
            />
          </Tooltip>
        )}
      </div>
      {invoiceNumber && (
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
          Invoice Number: {invoiceNumber}
        </div>
      )}

    </div>
  );
};

export default InvoiceFileBlock;
